import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { ResolvedSeo, SeoWarning } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';

import type { SeoPreview } from '../schemas/seo-preview';

export interface SeoPreviewCardProps {
  /** Computed from the unsaved form; null when the store settings are not readable. */
  live: SeoPreview | null;
  /** The saved entity's resolved SEO, or null before the first save. */
  saved: ResolvedSeo | null;
  /** Store path shown when there is no canonical URL yet, e.g. `/pages/about`. */
  path: string;
  /** No card around it, for a host that already frames it (a modal). */
  bare?: boolean;
}

function Warnings({ warnings }: { warnings: readonly SeoWarning[] }) {
  const { t } = useTranslation('seo');
  if (warnings.length === 0) {
    return (
      <p className="text-success flex items-center gap-1.5 text-sm">
        <CheckCircle2 className="size-4" aria-hidden />
        {t('preview.noWarnings')}
      </p>
    );
  }
  return (
    <ul aria-label={t('preview.warnings')} className="flex flex-col gap-1.5">
      {warnings.map((warning) => (
        <li key={warning} className="flex items-start gap-1.5 text-sm">
          <AlertTriangle className="text-warning mt-0.5 size-4 shrink-0" aria-hidden />
          {t(`warning.${warning}`)}
        </li>
      ))}
    </ul>
  );
}

/**
 * A search-result style preview of what the storefront will put in `<title>`, the meta
 * description, the canonical link and robots, with the backend's warnings in plain words.
 */
export function SeoPreviewCard({ live, saved, path, bare = false }: SeoPreviewCardProps) {
  const { t } = useTranslation('seo');
  const shown = live ?? saved;

  const preview = shown ? (
    <div className="flex flex-col gap-4" data-testid="seo-preview">
      <div className="bg-muted/40 flex flex-col gap-1 rounded-md border p-3">
        {bare ? (
          <span className="text-muted-foreground text-xs font-medium">
            {t('preview.inResults')}
          </span>
        ) : null}
        <span className="text-muted-foreground truncate font-mono text-xs">
          {shown.canonical ?? path}
        </span>
        <span className="text-primary text-base leading-snug font-medium break-words">
          {shown.title || t('preview.untitled')}
        </span>
        <span className="text-muted-foreground text-sm break-words">
          {shown.description ?? t('preview.noDescription')}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground">{t('preview.robots')}</span>
        <Badge variant={shown.robots.startsWith('noindex') ? 'secondary' : 'outline'}>
          {shown.robots}
        </Badge>
      </div>
      <Warnings warnings={shown.warnings} />
    </div>
  ) : (
    <p className="text-muted-foreground text-sm">{t('preview.afterSave')}</p>
  );
  if (bare) return preview;

  return (
    <FormSection
      title={t('preview.title')}
      description={live ? t('preview.live') : saved ? t('preview.saved') : t('preview.none')}
    >
      {preview}
    </FormSection>
  );
}
